import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArtworkReactions from "../components/ArtworkReactions";
import ArtworkFillDisplay from "../components/ArtworkFillDisplay";
import KidBackground from "../ui/backgrounds/KidBackground";
import { useAuth } from "../context/AuthContext";
import { getReactionArtworkId } from "../utils/reactionArtworkId";
import { resolveImageUrl } from "../utils/urlResolver";
import {
  coLearningGalleryPickerBackdropProps,
  coLearningInkBody,
  coLearningInkLink,
  coLearningInkMuted,
  coLearningInkTitle,
} from "../ui/coLearningModalTheme";
import {
  ArrowLeft,
  Home,
  LayoutDashboard,
  Images,
  Search,
  User,
  Menu,
  LogOut,
} from "lucide-react";
import { artNavPillBtn } from "../ui/artmosphereButtonTheme";

const pillNavBtn = artNavPillBtn;

const artmosphereTags = [
  "Around the world with 100 artists",
  "What's your story",
  "Complete the story",
  "Impressionism",
  "Pointillism",
];

export default function GeneralExhibit() {
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [artistProfiles, setArtistProfiles] = useState([]);
  const [selectedTag, setSelectedTag] = useState("All");
  const [searchArtist, setSearchArtist] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, logout } = useAuth();

  const artworkId = searchParams.get("artworkId");

  const handleSignOut = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    if (!navMenuOpen) return;
    const close = (e) => {
      if (!e.target.closest(".general-exhibit-nav-menu")) setNavMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [navMenuOpen]);

  useEffect(() => {
    const fetchArtworks = async () => {
      const artworksRef = collection(db, "artworks");
      const q = query(artworksRef, where("visibility", "==", "public"));
      const querySnapshot = await getDocs(q);
      const filtered = querySnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((art) => {
          const tag = art.tag || "";
          const source = art.source || "";
          const category = art.category || "";
          // Only artworks explicitly shared for General Exhibit
          if (source === "general" || category === "general") return true;
          // Exclude all activity / co-learning uploads
          if (
            source === "book-co-learning" ||
            source === "ways-of-seeing" ||
            source === "art-history" ||
            source === "daily-activity" ||
            art.isBookArtwork ||
            art.bookArtworkId ||
            art.activityId ||
            art.styleId ||
            art.styleName
          ) {
            return false;
          }
          // Legacy general uploads (before source field): no book tag, not an activity
          return !tag || !artmosphereTags.includes(tag);
        });

      setArtworks(filtered);
      setFilteredArtworks(filtered);
    };

    fetchArtworks();
  }, []);

  useEffect(() => {
    const fetchProfiles = async () => {
      const snapshot = await getDocs(collection(db, "artistProfiles"));
      const profiles = snapshot.docs.map((docSnap) => ({
        uid: docSnap.data().uid,
        name: docSnap.data().name || docSnap.data().nickname,
      }));
      setArtistProfiles(profiles);
    };
    fetchProfiles();
  }, []);

  const getArtistName = (uid) => {
    const profile = artistProfiles.find((p) => p.uid === uid);
    return profile?.name || "Unknown Artist";
  };

  const getArtImageUrl = (art) =>
    resolveImageUrl(art.thumbnailUrl || art.imageUrl || art.artworkUrl) ||
    art.thumbnailUrl ||
    art.imageUrl ||
    art.artworkUrl;

  useEffect(() => {
    let result = artworks;
    if (selectedTag !== "All") {
      result = result.filter((art) => art.tag === selectedTag);
    }
    if (searchArtist.trim() !== "") {
      result = result.filter((art) =>
        getArtistName(art.uid).toLowerCase().includes(searchArtist.toLowerCase())
      );
    }
    setFilteredArtworks(result);
  }, [selectedTag, searchArtist, artworks, artistProfiles]);

  useEffect(() => {
    if (!artworkId || filteredArtworks.length === 0) return;
    const targetArtwork = filteredArtworks.find((art) => art.id === artworkId);
    if (!targetArtwork) return;
    const artworkElement = document.getElementById(`artwork-${artworkId}`);
    if (!artworkElement) return;
    const timer = setTimeout(() => {
      artworkElement.scrollIntoView({ behavior: "smooth", block: "center" });
      artworkElement.classList.add("ring-4", "ring-amber-400", "ring-opacity-75");
      setTimeout(() => {
        artworkElement.classList.remove("ring-4", "ring-amber-400", "ring-opacity-75");
      }, 3000);
    }, 500);
    return () => clearTimeout(timer);
  }, [artworkId, filteredArtworks]);

  const availableTags = [
    "All",
    ...new Set(
      artworks.map((art) => art.tag).filter((tag) => tag && !artmosphereTags.includes(tag))
    ),
  ];

  return (
    <div className="artm-page artm-page-pad relative flex min-h-screen flex-col overflow-x-hidden">
      <KidBackground variant="aurora" intensity={0.38} className="z-0" />

      <div className="relative z-10 mx-auto flex min-h-0 w-full max-w-[1400px] flex-1 flex-col px-2 py-2 md:px-4 md:py-4 lg:py-5">
        <style>{`
          @keyframes generalExhibitFadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border-0 md:rounded-[36px] md:border md:border-[#F6D57B]/80 md:shadow-[0_24px_70px_rgba(120,72,20,0.12)]"
          style={{
            background: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            animation: "generalExhibitFadeIn 0.55s ease-out forwards",
          }}
        >
          {/* Header */}
          <header className="relative z-30 flex-shrink-0 border-b border-[#F5D99B]/50 bg-[#FFFDF9]/80 px-4 py-2.5 backdrop-blur-md sm:px-6 md:px-8">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.28),transparent_70%)]"
              aria-hidden
            />
            <div className="relative flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-2 sm:items-center">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={pillNavBtn}
                  title="Back"
                >
                  <ArrowLeft className="h-3.5 w-3.5 shrink-0 text-[#8A6A44]" strokeWidth={1.75} />
                  <span>Back</span>
                </button>
                <div className="min-w-0">
                  <h1 className="font-serif text-lg font-semibold leading-tight tracking-tight text-[#2F251B] sm:text-xl md:text-2xl">
                    General Exhibit
                  </h1>
                  <p className="mt-0.5 text-xs leading-snug text-[#6B4E32] sm:text-[13px]">
                    Art shared freely — beyond ARTmosphere book activities.
                  </p>
                </div>
              </div>
              <div className="general-exhibit-nav-menu relative flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setNavMenuOpen((open) => !open)}
                  className={pillNavBtn}
                  title="Menu"
                  aria-expanded={navMenuOpen}
                  aria-haspopup="menu"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-4 w-4 shrink-0 text-[#8A6A44]" strokeWidth={2} aria-hidden />
                </button>
                {navMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-50 mt-2 min-w-[11.5rem] overflow-hidden rounded-2xl border border-[#F6D57B]/90 bg-[#FFFDF9] py-1.5 shadow-[0_12px_32px_rgba(120,72,20,0.14)]"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setNavMenuOpen(false); navigate("/home"); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                    >
                      <Home className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                      Home
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setNavMenuOpen(false); navigate("/my-art-world"); }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                    >
                      <LayoutDashboard className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                      Artist Corner
                    </button>
                    {isAuthenticated ? (
                      <>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setNavMenuOpen(false); navigate("/artist-wall"); }}
                          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                        >
                          <User className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                          My Gallery
                        </button>
                        <div className="my-1 border-t border-[#F5D99B]/50" aria-hidden />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setNavMenuOpen(false); handleSignOut(); }}
                          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                        >
                          <LogOut className="h-4 w-4 text-[#8A6A44]" strokeWidth={1.75} aria-hidden />
                          Sign out
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="my-1 border-t border-[#F5D99B]/50" aria-hidden />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setNavMenuOpen(false); navigate("/login"); }}
                          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                        >
                          Login
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setNavMenuOpen(false); navigate("/signup"); }}
                          className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-[#5C4428] transition hover:bg-[#FFF3E0]"
                        >
                          Join Community
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Intro card */}
            <div className="px-3 pt-4 sm:px-6 md:px-8 md:pt-5">
              <div className="mx-auto flex max-w-4xl flex-col gap-3 rounded-2xl border border-[#F6D57B]/70 bg-gradient-to-br from-[#FFFDF8] via-[#FFF7ED] to-[#FFEFD6] p-4 shadow-[0_8px_28px_rgba(120,72,20,0.08)] sm:flex-row sm:items-center sm:gap-4 sm:p-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[#F5D99B]/60 bg-white/80 shadow-sm">
                  <Images className="h-6 w-6 text-[#B45309]" strokeWidth={1.75} aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className={`text-base sm:text-lg ${coLearningInkTitle}`}>
                    Welcome to the General Exhibit
                  </h2>
                  <p className={`mt-1 text-sm leading-relaxed ${coLearningInkMuted}`}>
                    Discover artworks young artists chose to share publicly — creative expression
                    outside book activities and museum wings. React to pieces you love.
                  </p>
                </div>
                <div className="shrink-0 text-center sm:text-right">
                  <p className="text-2xl font-semibold text-[#B45309]">{filteredArtworks.length}</p>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-[#8A6A44]">
                    {filteredArtworks.length === 1 ? "Artwork" : "Artworks"}
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="px-3 py-4 sm:px-6 md:px-8">
              <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
                <div className="relative flex-1 sm:max-w-xs">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A6A44]"
                    aria-hidden
                  />
                  <input
                    type="text"
                    placeholder="Search by artist name…"
                    value={searchArtist}
                    onChange={(e) => setSearchArtist(e.target.value)}
                    className="w-full rounded-full border border-[#F5D99B] bg-white/90 py-2 pl-9 pr-4 text-sm text-[#2F251B] shadow-sm placeholder:text-[#8A6A44]/70 focus:border-[#E8C97A] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/25"
                  />
                </div>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="rounded-full border border-[#F5D99B] bg-white/90 px-4 py-2 text-sm text-[#2F251B] shadow-sm focus:border-[#E8C97A] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/25 sm:w-48"
                >
                  {availableTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Grid */}
            <div className="px-3 pb-10 sm:px-6 md:px-8 md:pb-12">
              {filteredArtworks.length === 0 ? (
                <div
                  className="mx-auto max-w-lg overflow-hidden rounded-3xl border border-[#F6D57B]/70 bg-gradient-to-br from-[#FFFDF8] via-[#FFF7ED] to-[#FFEFD6] px-6 py-10 text-center shadow-[0_10px_32px_rgba(120,72,20,0.08)] sm:px-10 sm:py-12"
                  role="status"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#B45309]/80">
                    General Exhibit
                  </p>
                  <h3 className={`mt-2 font-serif text-xl font-semibold tracking-tight sm:text-2xl ${coLearningInkTitle}`}>
                    {searchArtist.trim()
                      ? "No matching artists"
                      : artworks.length === 0
                        ? "The gallery awaits its first piece"
                        : "Nothing matches your filters"}
                  </h3>
                  <p className={`mx-auto mt-3 max-w-sm text-sm leading-relaxed ${coLearningInkMuted}`}>
                    {searchArtist.trim()
                      ? `We couldn’t find an artist named “${searchArtist.trim()}” in the General Exhibit. Try another name or clear your search.`
                      : artworks.length === 0
                        ? "When young artists share work publicly, it appears here for everyone to discover and appreciate."
                        : "Try a different tag or choose “All” to browse the full exhibit."}
                  </p>
                  {searchArtist.trim() ? (
                    <button
                      type="button"
                      onClick={() => setSearchArtist("")}
                      className="mt-6 rounded-full border border-[#E8C97A] bg-white/90 px-5 py-2 text-sm font-medium text-[#5C4428] shadow-sm transition hover:bg-[#FFF3E0]"
                    >
                      Clear search
                    </button>
                  ) : isAuthenticated && artworks.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => navigate("/artist-wall")}
                      className="mt-6 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      Share from My Gallery
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                  {filteredArtworks.map((art) => (
                    <article
                      key={art.id}
                      id={`artwork-${art.id}`}
                      className="group overflow-hidden rounded-2xl border border-[#F6D57B]/60 bg-white/[0.92] shadow-[0_8px_24px_rgba(120,72,20,0.07)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(120,72,20,0.12)]"
                    >
                      <div className="relative">
                        <ArtworkFillDisplay
                          src={getArtImageUrl(art)}
                          alt={art.title || "Artwork"}
                          containerClassName="aspect-[4/5] w-full min-h-[220px] cursor-pointer"
                          onExpandClick={() => setSelectedPost(art)}
                        />
                        <div
                          className="absolute bottom-2 right-2 z-20"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ArtworkReactions
                            artworkId={getReactionArtworkId(art) || art.id}
                            artistId={art.uid}
                            enableComments={false}
                            listLayout="dropdown"
                            variant="premium"
                          />
                        </div>
                      </div>
                      <div className="border-t border-[#F5D99B]/40 p-3 sm:p-4">
                        <h2
                          className={`truncate text-base font-semibold ${coLearningInkTitle}`}
                          title={art.title}
                        >
                          {art.title || "Untitled"}
                        </h2>
                        <p className={`mt-1 text-sm ${coLearningInkMuted}`}>
                          <button
                            type="button"
                            onClick={() => navigate(`/artist-public-gallery/${art.uid}`)}
                            className={coLearningInkLink}
                          >
                            {getArtistName(art.uid)}
                          </button>
                        </p>
                        {art.tag && (
                          <span className="mt-2 inline-block rounded-full border border-[#F5D99B]/80 bg-[#FFF9EF] px-2 py-0.5 text-[10px] font-medium text-[#8A6A44]">
                            {art.tag}
                          </span>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {selectedPost && (
        <div {...coLearningGalleryPickerBackdropProps} onClick={() => setSelectedPost(null)}>
          <div
            className="relative mx-auto flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#e8e0d4] bg-white/95 shadow-2xl backdrop-blur-xl md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative md:w-2/3">
              <ArtworkFillDisplay
                src={getArtImageUrl(selectedPost)}
                alt={selectedPost.title || "Artwork"}
                containerClassName="h-[min(50vh,520px)] w-full min-h-[280px] md:h-[80vh]"
              />
              <div
                className="absolute bottom-3 right-3 z-20"
                onClick={(e) => e.stopPropagation()}
              >
                <ArtworkReactions
                  artworkId={getReactionArtworkId(selectedPost) || selectedPost.id}
                  artistId={selectedPost.uid}
                  enableComments={false}
                  listLayout="dropdown"
                  variant="premium"
                />
              </div>
            </div>
            <div className="relative max-h-[80vh] overflow-y-auto p-5 md:w-1/3 md:p-6">
              <button
                type="button"
                onClick={() => setSelectedPost(null)}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-[#e8e0d4] bg-white text-xl leading-none text-[#141210] hover:bg-stone-50"
                aria-label="Close"
              >
                ×
              </button>
              <h2 className={`pr-10 text-xl ${coLearningInkTitle}`}>{selectedPost.title}</h2>
              {selectedPost.description && (
                <p className={`mt-3 text-sm ${coLearningInkBody}`}>
                  <strong className="text-[#141210]">Description: </strong>
                  {selectedPost.description}
                </p>
              )}
              {selectedPost.technique && (
                <p className={`mt-2 text-sm ${coLearningInkBody}`}>
                  <strong className="text-[#141210]">Technique: </strong>
                  {selectedPost.technique}
                </p>
              )}
              {selectedPost.inspiration && (
                <p className={`mt-2 text-sm ${coLearningInkBody}`}>
                  <strong className="text-[#141210]">Inspiration: </strong>
                  {selectedPost.inspiration}
                </p>
              )}
              {selectedPost.tag && (
                <p className={`mt-2 text-sm ${coLearningInkBody}`}>
                  <strong className="text-[#141210]">Collection: </strong>
                  {selectedPost.tag}
                </p>
              )}
              <p className={`mt-4 text-sm ${coLearningInkBody}`}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPost(null);
                    navigate(`/artist-public-gallery/${selectedPost.uid}`);
                  }}
                  className={coLearningInkLink}
                >
                  Artist: {getArtistName(selectedPost.uid)}
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
